/** One copy field’s language versions, corrected against its stored source.
 * Save stays mounted until the host settles the write; refusal retains the
 * correction. Translation updates untouched locales and preserves local edits.
 */
// Side-effect: registers <schmancy-scroll>.
import "@mhmo91/schmancy/layout";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { type Locale, LocaleEnum, type LocalizedText } from "@momo/i18n";
import { html } from "lit";
import { property, state as stateDecorator } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import {
	animationFrameScheduler,
	auditTime,
	filter,
	from,
	fromEvent,
	map,
	merge,
	Subject,
	switchMap,
	takeUntil,
	tap,
} from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";

const LOCALES = LocaleEnum.options;

/** The same map `localize-copy.view.ts` keeps for its own panes — repeated
 *  because importing the whole bulk panel for two strings would couple the
 *  two surfaces' lifetimes. */
const LOCALE_LABEL_KEY = {
	de: "admin.events.localize.localeDe",
	en: "admin.events.localize.localeEn",
} as const;

@schmancyElement("localized-text-editor")
export class LocalizedTextEditor extends LocalizeMixin(SchmancyElement) {
	/** The field's own label, as the mounting form shows it ("Description"). */
	@property() label = "";
	/** The stored source string — the operator's own words. */
	@property() source = "";
	@property({ attribute: false }) byLocale: LocalizedText | null = null;
	/** A background pass is writing the versions right now. */
	@property({ type: Boolean }) pending = false;
	/** Prose by default; false for a single-line field (a ticket type's name). */
	@property({ type: Boolean }) multiline = true;
	/** Per-language cap (a ticket type's description) — over it, Save refuses. */
	@property({ type: Number }) maxlength?: number;

	@stateDecorator() private requesting = false;
	@stateDecorator() private requestFailed = false;
	@stateDecorator() private saving = false;
	@stateDecorator() private saveFailed = false;
	@stateDecorator() private active: Locale = "de";
	@stateDecorator() private drafts: Partial<Record<Locale, string>> = {};

	/** Locales the operator has edited this session — a background write that
	 *  lands while the overlay is open must not overtype their edit. */
	private readonly touched = new Set<Locale>();
	private wasPending = false;
	private readonly originalPane = createRef<HTMLElement>();
	private readonly versionPane = createRef<HTMLElement>();
	private readonly activeChanged$ = new Subject<void>();

	override willUpdate(changed: Map<PropertyKey, unknown>) {
		super.willUpdate(changed);
		if (changed.has("pending") && this.pending) this.wasPending = true;
		if (changed.has("byLocale")) {
			const next = { ...this.drafts };
			for (const locale of LOCALES) {
				if (!this.touched.has(locale))
					next[locale] = this.byLocale?.[locale] ?? "";
			}
			this.drafts = next;
		}
	}

	override connectedCallback() {
		super.connectedCallback();
		// Proportional scroll sync, ONE leader per gesture. Pointer, wheel and
		// key are every way an operator can move a pane, and each names the pane
		// it moved in its own composedPath — a scroll event cannot say whether it
		// came from the operator or from this pipeline's own write, so the
		// gesture picks the leader and the follower's scroll is ignored until the
		// next gesture lands. Two-way sync with echo filtering cannot hold here:
		// `<schmancy-scroll>` scrolls smoothly, so one write emits a frame-by-frame
		// stream of intermediate positions, every one of which reads as a fresh
		// gesture and drags the leader back. `instant` for the same reason — an
		// animated follower lags the leader visibly and is a scroll source of its
		// own.
		merge(
			merge(
				fromEvent(this.renderRoot, "pointerdown", {
					capture: true,
					passive: true,
				}),
				fromEvent(this.renderRoot, "wheel", { capture: true, passive: true }),
				fromEvent(this.renderRoot, "keydown", { capture: true, passive: true }),
			).pipe(
				map((e) => {
					const original = this.originalPane.value;
					const version = this.versionPane.value;
					if (original === undefined || version === undefined) return null;
					const path = e.composedPath();
					if (path.includes(original))
						return { leader: original, follower: version };
					if (path.includes(version))
						return { leader: version, follower: original };
					return null;
				}),
				filter(
					(panes): panes is { leader: HTMLElement; follower: HTMLElement } =>
						panes !== null,
				),
				switchMap((panes) =>
					fromEvent(panes.leader, "scroll", { passive: true }).pipe(
						map(() => panes),
					),
				),
			),
			// A tab switch mounts a fresh version pane at the top; the original is
			// the fixed reference, so it leads that one reconciliation.
			this.activeChanged$.pipe(
				switchMap(() => from(this.updateComplete)),
				map(() => ({
					leader: this.originalPane.value,
					follower: this.versionPane.value,
				})),
			),
		)
			.pipe(
				auditTime(0, animationFrameScheduler),
				map(({ leader, follower }) => {
					if (leader === undefined || follower === undefined) return null;
					const leaderRange = leader.scrollHeight - leader.clientHeight;
					const followerRange = follower.scrollHeight - follower.clientHeight;
					if (leaderRange <= 0 || followerRange <= 0) return null;
					return {
						follower,
						top: (leader.scrollTop / leaderRange) * followerRange,
					};
				}),
				filter(
					(cmd): cmd is { follower: HTMLElement; top: number } => cmd !== null,
				),
				// A late frame can re-report the leader's same position; the follower
				// already holds it, and writing it again is a scroll of its own.
				filter(({ follower, top }) => Math.abs(follower.scrollTop - top) >= 1),
				tap(({ follower, top }) =>
					follower.scrollTo({ top, behavior: "instant" }),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	/** Asks for the versions to be written again over the same source. Leaves
	 *  the overlay open on purpose: the pass lands here, in place, and this is
	 *  the surface the operator is watching it land on. */
	private readonly askForPass = () => {
		if (this.requesting || this.saving) return;
		this.requesting = true;
		this.dispatchEvent(
			new CustomEvent("translate", {
				detail: {
					settle: (accepted: boolean) => {
						this.requesting = false;
						this.requestFailed = !accepted;
					},
				},
				bubbles: true,
				composed: true,
			}),
		);
	};

	private readonly save = () => {
		if (this.saving) return;
		this.saving = true;
		const corrected: LocalizedText = Object.fromEntries(
			LOCALES.flatMap((locale) => {
				const version = (this.drafts[locale] ?? "").trim();
				return version.length > 0 ? [[locale, version] as const] : [];
			}),
		);
		this.dispatchEvent(
			new CustomEvent("save", {
				detail: {
					byLocale: Object.keys(corrected).length > 0 ? corrected : null,
					settle: (accepted: boolean) => {
						this.saving = false;
						this.saveFailed = !accepted;
						if (accepted)
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							);
					},
				},
				bubbles: true,
				composed: true,
			}),
		);
	};

	override render() {
		// Anchored `^…$` under `m`: only a line that holds the mark and nothing
		// else divides the source, so a `//` inside a line — a URL, a date range,
		// an aside — leaves the source whole.
		const parts = this.source
			.split(/^\s*\/\/\s*$/m)
			.map((part) => part.trim())
			.filter((part) => part.length > 0);
		const max = this.maxlength;
		const overCap = LOCALES.filter(
			(locale) =>
				max !== undefined && (this.drafts[locale] ?? "").trim().length > max,
		);
		const dirty = LOCALES.some(
			(locale) =>
				(this.drafts[locale]?.trim() ?? "") !== (this.byLocale?.[locale] ?? ""),
		);
		const overCapMessage =
			max !== undefined && overCap.length > 0
				? this.t("admin.events.localize.tooLong", {
						max,
						count: Math.max(
							...overCap.map(
								(locale) => (this.drafts[locale] ?? "").trim().length,
							),
						),
					})
				: null;
		return html`
			<div
				class=${this.classMap({
					"flex flex-col": true,
					"w-[min(94vw,54rem)]": this.multiline,
					"w-[min(94vw,40rem)]": !this.multiline,
				})}
			>
				<div class="flex flex-col gap-1 p-5 pb-3 md:p-8 md:pb-4">
					<schmancy-typography type="title" token="md">
						${this.label}
					</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.t("admin.events.localize.autoNote")}
					</schmancy-typography>
					<schmancy-visually-hidden role="status" aria-live="polite">
						${
							this.pending
								? this.t("admin.events.localize.pendingNote")
								: this.wasPending
									? this.t("admin.events.localize.readyNote")
									: ""
						}
					</schmancy-visually-hidden>
				</div>

				<div ?inert=${this.saving} class="grid grid-cols-2 gap-x-5 px-5 md:gap-x-8 md:px-8">
					<div class="flex flex-col">
						<div class="flex h-10 items-center">
							<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
								${this.t("admin.events.localize.originalLabel")}
							</schmancy-typography>
						</div>
						<schmancy-scroll
							${ref(this.originalPane)}
							direction="vertical"
							fade
							style="--schmancy-scroll-fade-block: var(--schmancy-sys-typescale-body-md-line-height, 20px)"
							role="region"
							aria-label=${this.t("admin.events.localize.originalLabel")}
							tabindex="0"
							class="max-h-[52dvh] pb-3"
						>
							${when(
								parts.length > 1,
								() => html`
									<div class="flex flex-col gap-4">
										${repeat(
											parts,
											(_, index) => index,
											(part, index) => html`
												${when(
													index > 0,
													() => html`
														<div class="flex flex-col gap-2">
															<schmancy-divider></schmancy-divider>
															${when(
																index === 1,
																() => html`
																	<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
																		${this.t("admin.events.localize.originalSeparatorNote")}
																	</schmancy-typography>
																`,
															)}
														</div>
													`,
												)}
												<schmancy-typography
													type="body"
													token="md"
													class="whitespace-pre-wrap"
													>${part}</schmancy-typography
												>
											`,
										)}
									</div>
								`,
								() => html`<schmancy-typography
									type="body"
									token="md"
									class="whitespace-pre-wrap"
									>${this.source}</schmancy-typography
								>`,
							)}
						</schmancy-scroll>
					</div>

					<schmancy-tab-group
						.activeTab=${this.active}
						label=${this.t("admin.events.localize.openButton")}
						@tab-changed=${(e: CustomEvent<string>) => {
							if (e.detail === this.active) return;
							const next = LOCALES.find((locale) => locale === e.detail);
							if (next === undefined) return;
							this.active = next;
							this.activeChanged$.next();
						}}
					>
						${repeat(
							LOCALES,
							(locale) => locale,
							(locale) => html`
								<schmancy-tab
									label=${this.t(LOCALE_LABEL_KEY[locale])}
									value=${locale}
								>
									${when(
										this.active === locale,
										() => html`
											<schmancy-scroll
												${ref(this.versionPane)}
												direction="vertical"
												fade
												style="--schmancy-scroll-fade-block: var(--schmancy-sys-typescale-body-md-line-height, 20px)"
												class="max-h-[52dvh] pb-3"
											>
												<schmancy-typography-field
													type="body"
													token="md"
													?multiline=${this.multiline}
													.value=${this.drafts[locale] ?? ""}
													placeholder=${this.t("admin.events.localize.emptyPlaceholder")}
													label=${`${this.label} — ${this.t(LOCALE_LABEL_KEY[locale])}`}
													@input=${(e: CustomEvent<{ value: string }>) => {
														if (e.detail.value === (this.drafts[locale] ?? ""))
															return;
														this.touched.add(locale);
														this.drafts = {
															...this.drafts,
															[locale]: e.detail.value,
														};
													}}
												></schmancy-typography-field>
											</schmancy-scroll>
										`,
									)}
								</schmancy-tab>
							`,
						)}
					</schmancy-tab-group>
				</div>

				<schmancy-surface
					opacity="translucent"
					class="flex flex-wrap items-center justify-between gap-4 px-5 pb-4 pt-4 md:px-8 md:pb-6"
				>
					<div class="flex items-center gap-3">
						<schmancy-button variant="text" .busy=${this.requesting} ?disabled=${this.saving} color=${this.requestFailed ? "error" : "primary"} @click=${this.askForPass}>
							<schmancy-icon slot="prefix" aria-hidden="true">translate</schmancy-icon>
							<span style="display:grid"><span style=${this.requestFailed ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.requestFailed ? "true" : "false"}>${this.t("admin.events.localize.fieldTranslateButton")}</span><span style=${this.requestFailed ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.requestFailed ? "false" : "true"}>${this.t("admin.common.retry")}</span></span>
						</schmancy-button>
						${when(
							overCapMessage !== null,
							() => html`
								<schmancy-typography type="label" token="sm" class="text-error-default">
									${overCapMessage}
								</schmancy-typography>
							`,
						)}
					</div>
					<schmancy-button
						variant="filled"
						?disabled=${!dirty || overCap.length > 0}
						.busy=${this.saving}
						color=${this.saveFailed ? "error" : "primary"}
						@click=${this.save}
					>
						<span style="display:grid"><span style=${this.saveFailed ? "grid-area:1/1;visibility:hidden" : "grid-area:1/1"} aria-hidden=${this.saveFailed ? "true" : "false"}>${this.t("admin.common.save")}</span><span style=${this.saveFailed ? "grid-area:1/1" : "grid-area:1/1;visibility:hidden"} aria-hidden=${this.saveFailed ? "false" : "true"}>${this.t("admin.common.retrySave")}</span></span>
					</schmancy-button>
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"localized-text-editor": LocalizedTextEditor;
	}
}
